import {
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  Switch,
} from '@mui/material';
import type { DlsgPolicyConfig } from '@repo/api-gw-sdk';
import {
  HighFrequencyBackupScheduleFrequency,
  Provider,
} from '@repo/api-gw-sdk';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { v4 as uuid } from 'uuid';

import { RetentionPopover } from '@/app/(dashboard)/settings/controls/retentionSelectionPopover';
import {
  dailyFrequency,
  hourlyFrequency,
} from '@/app/(dashboard)/settings/frequency';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { FormTextField } from '@/components/form/formTextField';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { ScheduleSelect } from '@/components/schedule/schedule';
import { BackupVaultSelectionDialog } from '@/components/vaults/backupVaultSelectionDialog';
import { VaultTag } from '@/components/vaults/vaultTag';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import { ResourcesSelectorWrapper } from './resourcesSelectorWrapper';
import {
  addIdsToDlsgPolicyConfig,
  removeIdsFromDlsgPolicy,
  type DlsgPolicyConfigWithIds,
  type DlsgPolicyWithIds,
  type StorageGatewayWizardState,
} from './storageGatewayWizardFlow';

import {
  defaultDlsgPolicy,
  defaultDlsgPolicyConfig,
  defaultHourlyDlsgPolicy,
} from '../defaultDlsgPolicy';

export const StorageGatewayEditStep = (
  props: StepProps<StorageGatewayWizardState>
) => {
  const { isDev, isTest } = useEnvironment();
  const dlsgPolicy = props.wizardState.dlsgPolicy;
  const [vaultsDialogOpen, setVaultsDialogOpen] = useState(false);
  const dal = useDAL();

  const { vaults } = useBackupVaults();
  const { data: vaultAccounts } = dal.vaults.useAccounts();

  const backupRetentionDays =
    dlsgPolicy.policyPlan.backupSchedule.backupRetentionDays;
  const vaultId = dlsgPolicy.policyPlan.backupSchedule.vaultId;
  const vault = vaults.find((v) => v.id === vaultId);
  const vaultAccount = vaultAccounts?.accounts.find(
    (account) => account.id === vault?.vaultAccountId
  );
  const isCustomerOwnedVault = vaultAccount?.isCustomerOwned ?? false;

  const replicationConfig =
    dlsgPolicy.policyPlan.vaultMetadataReplicationConfig;
  const replicationEnabled = Boolean(replicationConfig);

  return (
    <StepContainer
      sx={{ padding: '40px min(80px,4vw)' }}
      canGoNext={() => {
        const schedule = dlsgPolicy.policyPlan.backupSchedule;
        const configInvalid =
          props.wizardState.prefixesSelectionMode === 'specific' &&
          (!dlsgPolicy.policyPlan.dlsgConfig.length ||
            dlsgPolicy.policyPlan.dlsgConfig.some(
              (x) => !x.pathPrefixes.length
            ) ||
            !validatePrefixes(dlsgPolicy));

        const replicationConfigInvalid =
          isCustomerOwnedVault &&
          replicationEnabled &&
          (!replicationConfig?.replicationBucketId ||
            replicationConfig.replicationBucketId.trim() === '' ||
            !replicationConfig?.replicationRegion ||
            replicationConfig.replicationRegion.trim() === '');

        if (
          !dlsgPolicy.resourceInclusion.length ||
          !schedule ||
          schedule.backupRetentionDays < 1 ||
          !schedule.vaultId ||
          configInvalid ||
          replicationConfigInvalid
        ) {
          return false;
        }

        return true;
      }}
      nextButtonText={dlsgPolicy.id ? 'Save Changes' : 'Connect Resources'}
      onNextClick={async () => {
        let policy = { ...removeIdsFromDlsgPolicy(dlsgPolicy) };

        if (props.wizardState.prefixesSelectionMode === 'all') {
          policy = {
            ...policy,
            policyPlan: {
              ...policy.policyPlan,
              dlsgConfig: [
                {
                  ...props.wizardState.allObjectsPolicyConfig,
                  pathPrefixes: [''],
                },
              ],
            },
          };
        }

        /* fix till server will save interval config */
        const scheduleConfig =
          policy.policyPlan.backupSchedule.scheduleConfig.frequency ===
          HighFrequencyBackupScheduleFrequency.HIGH_FREQUENCY_BACKUP_SCHEDULE_INTERVAL
            ? defaultHourlyDlsgPolicy.policyPlan.backupSchedule.scheduleConfig
            : defaultDlsgPolicy.policyPlan.backupSchedule.scheduleConfig;
        policy.policyPlan.backupSchedule.scheduleConfig = scheduleConfig;
        /* fix till server will save interval config */

        if (policy.id) {
          await dal.storageGateway.update(policy.id, policy);
        } else {
          await dal.storageGateway.create({
            ...policy,
            enabled: true,
          });
        }

        return props.finish();
      }}
      navigationComponent={
        <RenameEntityName
          label='Gateway policy name'
          name={dlsgPolicy.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({
              ...state,
              dlsgPolicy: { ...state.dlsgPolicy, name },
            }))
          }
        />
      }
      backButtonText='Cancel'
      onBackClick={props.back}
      hideStepper
      toolbarComponent={
        <Stack>
          <Typography className='ml-[40px]' variant='body1' maxWidth='582px'>
            Define how your source data is migrated to Eon’s vault and made
            accessible through the Eon Gateway.
          </Typography>
          <Divider className='mt-[24px]' />
        </Stack>
      }
    >
      <Stack
        direction='row'
        divider={<Divider orientation='vertical' flexItem />}
        gap='min(80px,4vw)'
        flexGrow={1}
      >
        <Stack width='308px' gap='24px'>
          <Typography variant='h3'>Resource selection</Typography>
          <Typography variant='body1'>
            {`Select storage resources from your source region to migrate (added resources can’t be removed).`}
          </Typography>
          <ResourcesSelectorWrapper
            region={props.wizardState.regionName}
            disabledDeletion={!!dlsgPolicy.id && !isDev && !isTest}
            initialResourcesIds={props.wizardState.initialResourcesIds}
            selectedResourcesIds={dlsgPolicy.resourceInclusion}
            addResource={(providerResourceId) =>
              props.setWizardState((state) => ({
                ...state,
                dlsgPolicy: {
                  ...state.dlsgPolicy,
                  resourceInclusion: [
                    ...state.dlsgPolicy.resourceInclusion,
                    providerResourceId,
                  ],
                },
              }))
            }
            removeResource={(providerResourceId) =>
              props.setWizardState((state) => ({
                ...state,
                dlsgPolicy: {
                  ...state.dlsgPolicy,
                  resourceInclusion: state.dlsgPolicy.resourceInclusion.filter(
                    (r) => r !== providerResourceId
                  ),
                },
              }))
            }
          />
        </Stack>
        <Stack width='65%' gap='24px'>
          <Typography variant='h3'>Objects to migrate</Typography>
          <ToggleButtonGroup
            sx={{ width: 'max-content' }}
            exclusive
            value={props.wizardState.prefixesSelectionMode}
            onChange={(e, value) => {
              if (value === null) {
                // Keep the current selection
                return;
              }

              props.setWizardState((state) => ({
                ...state,
                dlsgPolicy: {
                  ...state.dlsgPolicy,
                  policyPlan: {
                    ...state.dlsgPolicy.policyPlan,
                    dlsgConfig: state.dlsgPolicy.policyPlan.dlsgConfig.map(
                      (x) => {
                        return {
                          ...x,
                          pathPrefixes:
                            value === 'specific' && !x.pathPrefixes?.length
                              ? [{ path: '', id: uuid() }]
                              : x.pathPrefixes,
                        };
                      }
                    ),
                  },
                },
                prefixesSelectionMode: value,
              }));
            }}
          >
            <ToggleButton value='all'>All objects</ToggleButton>
            <ToggleButton value='specific'>Specific prefixes</ToggleButton>
          </ToggleButtonGroup>
          {props.wizardState.prefixesSelectionMode === 'specific' ? (
            <SpecificPrefixes {...props} />
          ) : (
            <AllObjectsPolicyConfig {...props} />
          )}
        </Stack>
        <Stack width='308px' gap='24px'>
          <Typography variant='h3'>Storage & retention</Typography>
          <Typography variant='body1'>
            Select the vault and retention period for your migrated data, which
            remains accessible through the Gateway.
          </Typography>
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography noWrap variant='body1'>
              Store in
            </Typography>
            {dlsgPolicy.id ? (
              vault && <VaultTag vault={vault} />
            ) : (
              <ScheduleSelect
                sx={{
                  '& .MuiSelect-select:focus': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiTypography-root': {
                    color: 'var(--mui-palette-text-disabled)',
                  },
                }}
                data-testid='create-backup-policy-vault-select'
                variant='standard'
                value={vaultId || 0}
                open={false}
                native={false}
                onClick={() => setVaultsDialogOpen(true)}
              >
                {!vault && (
                  <MenuItem value={0}>
                    <Typography className='font-medium'>
                      Choose vault
                    </Typography>
                  </MenuItem>
                )}

                {vault && (
                  <MenuItem value={vault.id}>
                    <VaultTag
                      vault={vault}
                      sx={{ margin: 0, marginRight: '16px', cursor: 'pointer' }}
                    />
                  </MenuItem>
                )}
              </ScheduleSelect>
            )}
            <BackupVaultSelectionDialog
              vaultsPredicate={(vault) =>
                vault.region === props.wizardState.regionName &&
                vault.vaultAttributes.cloudProvider === Provider.Aws
              }
              isLoading={false}
              open={vaultsDialogOpen}
              onClose={() => setVaultsDialogOpen(false)}
              onSelect={(vault) => {
                props.setWizardState((state) => ({
                  ...state,
                  dlsgPolicy: {
                    ...state.dlsgPolicy,
                    policyPlan: {
                      ...state.dlsgPolicy.policyPlan,
                      backupSchedule: {
                        ...state.dlsgPolicy.policyPlan.backupSchedule,
                        vaultId: vault.id,
                      },
                    },
                  },
                }));
                setVaultsDialogOpen(false);
              }}
              scheduleWindow='resources'
              selectedVaults={
                dlsgPolicy.policyPlan.backupSchedule.vaultId
                  ? [dlsgPolicy.policyPlan.backupSchedule.vaultId]
                  : []
              }
            />
          </Stack>
          <Stack direction='row' alignItems='center' gap='8px'>
            <Typography variant='body1'>Retain for</Typography>
            <RetentionPopover
              initialRetentionInDays={backupRetentionDays}
              minimumRetentionInDays={
                props.wizardState.dlsgPolicy.policyPlan.backupSchedule
                  .scheduleConfig.frequency ===
                HighFrequencyBackupScheduleFrequency.HIGH_FREQUENCY_BACKUP_SCHEDULE_DAILY
                  ? dailyFrequency.minimumRetentionInDays
                  : hourlyFrequency.minimumRetentionInDays
              }
              onUpdate={(days) => {
                props.setWizardState((state) => ({
                  ...state,
                  dlsgPolicy: {
                    ...state.dlsgPolicy,
                    policyPlan: {
                      ...state.dlsgPolicy.policyPlan,
                      backupSchedule: {
                        ...state.dlsgPolicy.policyPlan.backupSchedule,
                        backupRetentionDays: days,
                      },
                    },
                  },
                }));
              }}
            />
          </Stack>
          {isCustomerOwnedVault && (
            <>
              <Divider />
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant='body1'>Replicate metadata</Typography>
                <Switch
                  checked={replicationEnabled}
                  onChange={() => {
                    const newReplicationConfig = replicationEnabled
                      ? undefined
                      : {
                          replicationBucketId: '',
                          replicationPrefix: '',
                          replicationRegion: '',
                        };
                    props.setWizardState((state) => ({
                      ...state,
                      dlsgPolicy: {
                        ...state.dlsgPolicy,
                        policyPlan: {
                          ...state.dlsgPolicy.policyPlan,
                          vaultMetadataReplicationConfig: newReplicationConfig,
                        },
                      },
                    }));
                  }}
                />
              </Stack>
              {replicationEnabled && (
                <>
                  <FormTextField
                    title='Bucket ID'
                    placeholder='Enter bucket ID'
                    value={replicationConfig?.replicationBucketId || ''}
                    validation={{
                      validateInput: (value: string) => value.trim() !== '',
                      errorMessage:
                        'Bucket ID is required when replication is enabled',
                    }}
                    onChange={(e) => {
                      const newBucketId = e.target.value;
                      props.setWizardState((state) => ({
                        ...state,
                        dlsgPolicy: {
                          ...state.dlsgPolicy,
                          policyPlan: {
                            ...state.dlsgPolicy.policyPlan,
                            vaultMetadataReplicationConfig: {
                              ...state.dlsgPolicy.policyPlan
                                .vaultMetadataReplicationConfig,
                              replicationBucketId: newBucketId,
                            },
                          },
                        },
                      }));
                    }}
                  />
                  <Stack>
                    <FormTextField
                      title='Prefix'
                      placeholder='Enter prefix'
                      value={replicationConfig?.replicationPrefix || ''}
                      onChange={(e) => {
                        const newPrefix = e.target.value;
                        props.setWizardState((state) => ({
                          ...state,
                          dlsgPolicy: {
                            ...state.dlsgPolicy,
                            policyPlan: {
                              ...state.dlsgPolicy.policyPlan,
                              vaultMetadataReplicationConfig: {
                                ...state.dlsgPolicy.policyPlan
                                  .vaultMetadataReplicationConfig,
                                replicationPrefix: newPrefix,
                              },
                            },
                          },
                        }));
                      }}
                    />
                    <Typography
                      paddingTop='8px'
                      variant='subtitle1'
                      sx={{ fontWeight: 600 }}
                      color='var(--mui-palette-secondary-main)'
                    >
                      Example: metadata-replication/
                    </Typography>
                  </Stack>
                  <FormTextField
                    title='Region'
                    placeholder='Enter region (e.g., us-east-1)'
                    value={replicationConfig?.replicationRegion || ''}
                    validation={{
                      validateInput: (value: string) => value.trim() !== '',
                      errorMessage:
                        'Region is required when replication is enabled',
                    }}
                    onChange={(e) => {
                      const newRegion = e.target.value;
                      props.setWizardState((state) => ({
                        ...state,
                        dlsgPolicy: {
                          ...state.dlsgPolicy,
                          policyPlan: {
                            ...state.dlsgPolicy.policyPlan,
                            vaultMetadataReplicationConfig: {
                              ...state.dlsgPolicy.policyPlan
                                .vaultMetadataReplicationConfig,
                              replicationRegion: newRegion,
                            },
                          },
                        },
                      }));
                    }}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </StepContainer>
  );
};

const SpecificPrefixes = (props: StepProps<StorageGatewayWizardState>) => {
  return (
    <Stack gap='24px'>
      {props.wizardState.dlsgPolicy.policyPlan.dlsgConfig.map((x) => (
        <DlsgPolicyConfigItem
          key={x.id}
          config={x}
          setWizardState={props.setWizardState}
          state={props.wizardState}
        />
      ))}
      <ButtonWithIcon
        disabled={props.wizardState.dlsgPolicy.policyPlan.dlsgConfig.some((x) =>
          x.pathPrefixes.some((y) => y.path === '/' || y.path === '')
        )}
        icon='material-symbols-add-rounded'
        text='Add Another'
        onClick={() => {
          props.setWizardState((state) => ({
            ...state,
            dlsgPolicy: {
              ...state.dlsgPolicy,
              policyPlan: {
                ...state.dlsgPolicy.policyPlan,
                dlsgConfig: [
                  ...(state.dlsgPolicy.policyPlan.dlsgConfig || []),
                  {
                    ...addIdsToDlsgPolicyConfig(defaultDlsgPolicyConfig),
                  },
                ],
              },
            },
          }));
        }}
      />
    </Stack>
  );
};

const AllObjectsPolicyConfig = (
  props: StepProps<StorageGatewayWizardState>
) => {
  const onRetentionChange = (days: number): void => {
    props.setWizardState((state) => ({
      ...state,
      allObjectsPolicyConfig: {
        ...state.allObjectsPolicyConfig,
        migrationDelayHours: days * 24,
      },
    }));
  };
  return (
    <Stack gap='24px'>
      <AgeSelection
        config={props.wizardState.allObjectsPolicyConfig}
        onRetentionChange={onRetentionChange}
      />
    </Stack>
  );
};

const DlsgPolicyConfigItem = ({
  config,
  setWizardState,
  state,
}: {
  config: DlsgPolicyConfigWithIds;
  setWizardState: Dispatch<SetStateAction<StorageGatewayWizardState>>;
  state: StorageGatewayWizardState;
}) => {
  const onRetentionChange = (days: number, configId: string): void => {
    setWizardState((state: StorageGatewayWizardState) => ({
      ...state,
      dlsgPolicy: {
        ...state.dlsgPolicy,
        policyPlan: {
          resourceTypes: state.dlsgPolicy.policyPlan.resourceTypes || [],
          backupSchedule: state.dlsgPolicy.policyPlan.backupSchedule,
          dlsgConfig: (state.dlsgPolicy.policyPlan.dlsgConfig || []).map(
            (config) =>
              config.id !== configId
                ? config
                : {
                    ...config,
                    migrationDelayHours: days * 24,
                  }
          ),
        },
      },
    }));
  };

  return (
    <Stack
      key={config.id}
      sx={{
        border: '1px solid var(--mui-palette-divider)',
        padding: '24px',
      }}
      direction='row'
      alignItems='flex-start'
      gap='40px'
    >
      <Stack flexGrow={1} gap='24px'>
        <AgeSelection
          config={config}
          onRetentionChange={(days) => onRetentionChange(days, config.id)}
        />
        <Stack gap='12px'>
          {config.pathPrefixes.map((prefix, prefixIndex, items) => (
            <PrefixRow
              key={prefix.id}
              prefix={prefix}
              canDelete={items.length > 1}
              onChange={(value) => {
                setWizardState((state: StorageGatewayWizardState) => ({
                  ...state,
                  dlsgPolicy: {
                    ...state.dlsgPolicy,
                    policyPlan: {
                      ...state.dlsgPolicy.policyPlan,
                      dlsgConfig: (
                        state.dlsgPolicy.policyPlan.dlsgConfig || []
                      ).map((conf) =>
                        conf.id !== config.id
                          ? conf
                          : {
                              ...conf,
                              pathPrefixes: conf.pathPrefixes.map((x) =>
                                x.id === prefix.id ? { ...x, path: value } : x
                              ),
                            }
                      ),
                    },
                  },
                }));
              }}
              onDelete={() => {
                setWizardState((state) => ({
                  ...state,
                  dlsgPolicy: {
                    ...state.dlsgPolicy,
                    policyPlan: {
                      ...state.dlsgPolicy.policyPlan,
                      dlsgConfig: (
                        state.dlsgPolicy.policyPlan.dlsgConfig || []
                      ).map((conf) =>
                        conf.id !== config.id
                          ? conf
                          : {
                              ...conf,
                              pathPrefixes: conf.pathPrefixes.filter(
                                (x) => x.id !== prefix.id
                              ),
                            }
                      ),
                    },
                  },
                }));
              }}
            />
          ))}
        </Stack>
      </Stack>
      <Stack direction='row' alignItems='center' gap='8px'>
        <IconButton
          disabled={state.dlsgPolicy.policyPlan.dlsgConfig.some((x) =>
            x.pathPrefixes.some((y) => y.path === '' || y.path === '/')
          )}
          size='small'
          color='primary'
          onClick={() => {
            setWizardState((state) => ({
              ...state,
              dlsgPolicy: {
                ...state.dlsgPolicy,
                policyPlan: {
                  ...state.dlsgPolicy.policyPlan,
                  dlsgConfig: (
                    state.dlsgPolicy.policyPlan.dlsgConfig || []
                  ).map((conf) =>
                    conf.id !== config.id
                      ? conf
                      : {
                          ...conf,
                          pathPrefixes: [
                            ...conf.pathPrefixes,
                            { path: '', id: uuid() },
                          ],
                        }
                  ),
                },
              },
            }));
          }}
        >
          <i className='material-symbols-add-rounded'></i>
        </IconButton>
        <OptionMenu
          options={['Delete']}
          onOptionSelected={(option) => {
            if (option === 'Delete') {
              setWizardState((state) => ({
                ...state,
                dlsgPolicy: {
                  ...state.dlsgPolicy,
                  policyPlan: {
                    ...state.dlsgPolicy.policyPlan,
                    dlsgConfig: (
                      state.dlsgPolicy.policyPlan.dlsgConfig || []
                    ).filter((conf) => conf !== config),
                  },
                },
              }));
            }
          }}
        />
      </Stack>
    </Stack>
  );
};

const DEFAULT_OBJECT_AGE_DAYS = 5;
const AgeSelection = ({
  config,
  onRetentionChange,
}: {
  config: DlsgPolicyConfig | DlsgPolicyConfigWithIds;
  onRetentionChange: (days: number) => void;
}) => {
  return (
    <Stack height='32px' direction='row' alignItems='center' gap='8px'>
      <Typography>Migrate objects</Typography>
      <Select
        variant='standard'
        value={
          config.migrationDelayHours ? 'objects-older-than' : 'from-all-time'
        }
        onChange={(event) =>
          onRetentionChange(
            event.target.value === 'objects-older-than'
              ? DEFAULT_OBJECT_AGE_DAYS
              : 0
          )
        }
      >
        <MenuItem value={'from-all-time'}>From all time</MenuItem>
        <MenuItem value={'objects-older-than'}>Objects older than</MenuItem>
      </Select>
      {!!config.migrationDelayHours && (
        <RetentionPopover
          popoverTitle='Object age'
          initialRetentionInDays={config.migrationDelayHours / 24}
          minimumRetentionInDays={1}
          onUpdate={onRetentionChange}
        />
      )}
    </Stack>
  );
};

const PrefixRow = ({
  prefix,
  canDelete,
  onChange,
  onDelete,
}: {
  prefix: { path: string; id: string };
  canDelete: boolean;
  onChange: (value: string) => void;
  onDelete: () => void;
}) => {
  const [value, setValue] = useState(prefix.path);
  const [touched, setTouched] = useState(false);

  const isError = (touched && value === '') || value === '/';

  return (
    <Stack direction='row' alignItems='center' gap='40px' flexGrow={1}>
      <Stack direction='row' alignItems='start' gap='12px' flexGrow={1}>
        <TextField
          error={isError}
          helperText={isError && 'Prefix cannot be empty'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            onChange(value);
            setTouched(true);
          }}
          className='flex-grow'
        />
        {canDelete && (
          <IconButton size='small' color='primary' onClick={onDelete}>
            <i className='material-symbols-close-rounded'></i>
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

const validatePrefixes = (policy: DlsgPolicyWithIds) => {
  const allPrefixes = policy.policyPlan.dlsgConfig
    .map((x) => x.pathPrefixes.map((y) => y.path))
    .flat()
    .sort();

  if (allPrefixes.some((x) => x === '' || x === '/')) {
    return false;
  }

  for (let i = 0; i < allPrefixes.length - 1; i++) {
    const current = trimSlash(allPrefixes[i]);
    const next = trimSlash(allPrefixes[i + 1]);

    if (next.startsWith(current)) {
      return false;
    }
  }

  return true;
};

const trimSlash = (value: string) => {
  const trimmed = value.endsWith('/') ? value.slice(0, -1) : value;
  return trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
};
